import { useState, useMemo, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Dialog } from '@/components/ui/Dialog';
import {
  DataTable,
  type ColumnDef,
  type SortState,
  type PaginationState,
} from '@/components/data-table';
import { apiClient, type ApiResponse } from '@/lib/api-client';
import { JOB_ROLE_OPTIONS } from '@/lib/constants';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface Team {
  id: string;
  name: string;
  description: string | null;
  parentTeamId: string | null;
  leadUserId: string | null;
  leadUserName: string | null;
  isActive: boolean;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

interface TeamMember {
  userId: string;
  name: string;
  email: string;
  jobRole: string;
  addedAt: string;
}

interface TeamDetail extends Team {
  members: TeamMember[];
}

interface UserOption {
  id: string;
  name: string;
  email: string;
}

interface CreateTeamData {
  name: string;
  description: string;
  parentTeamId: string | null;
  leadUserId: string | null;
}

interface UpdateTeamData extends CreateTeamData {
  isActive: boolean;
}

// ─── Main Component ─────────────────────────────────────────────────────────────

/**
 * TeamsTab — Teams CRUD + member management.
 * Requirements: REQ-5.2
 */
export function TeamsTab() {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [detailTeam, setDetailTeam] = useState<Team | null>(null);
  const [deletingTeam, setDeletingTeam] = useState<Team | null>(null);
  const [sort, setSort] = useState<SortState>({ column: 'name', direction: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState('');

  // ─── Queries ────────────────────────────────────────────────────────────────

  const { data: teamsData, isLoading } = useQuery({
    queryKey: ['settings', 'teams', { status: statusFilter, page, pageSize, sort }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (sort.direction) {
        params.set('sortBy', sort.column);
        params.set('sortDirection', sort.direction);
      }
      const response = await apiClient.get<
        ApiResponse<{
          teams: Team[];
          meta: { page: number; pageSize: number; totalCount: number };
        }>
      >(`/teams?${params.toString()}`);
      const { teams, meta } = response.data.data;
      return { teams, total: meta.totalCount, page: meta.page, pageSize: meta.pageSize };
    },
  });

  const { data: allTeams } = useQuery({
    queryKey: ['settings', 'teams-list'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<{ teams: Team[] }>>('/teams?pageSize=200');
      return response.data.data.teams;
    },
  });

  const { data: users } = useQuery({
    queryKey: ['settings', 'users-list'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<UserOption[]>>(
        '/users?pageSize=200&status=active',
      );
      return response.data.data;
    },
  });

  // ─── Mutations ──────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: async (data: CreateTeamData) => {
      const response = await apiClient.post<ApiResponse<Team>>('/teams', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'teams'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'teams-list'] });
      setIsFormOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTeamData }) => {
      const response = await apiClient.put<ApiResponse<Team>>(`/teams/${id}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'teams'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'teams-list'] });
      setIsFormOpen(false);
      setEditingTeam(null);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/teams/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'teams'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'teams-list'] });
      setDeletingTeam(null);
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await apiClient.put<ApiResponse<Team>>(`/teams/${id}`, { isActive });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'teams'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'teams-list'] });
    },
  });

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleNewTeam = () => {
    setEditingTeam(null);
    setIsFormOpen(true);
  };

  const handleEditTeam = (team: Team) => {
    setEditingTeam(team);
    setIsFormOpen(true);
  };

  const handleSaveTeam = (data: CreateTeamData | UpdateTeamData) => {
    if (editingTeam) {
      updateMutation.mutate({ id: editingTeam.id, data: data as UpdateTeamData });
    } else {
      createMutation.mutate(data as CreateTeamData);
    }
  };

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);
  const handleSortChange = useCallback((newSort: SortState) => {
    setSort(newSort);
    setPage(1);
  }, []);

  // ─── Columns ────────────────────────────────────────────────────────────────

  const columns: ColumnDef<Team>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div>
            <button
              type="button"
              className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
              onClick={() => setDetailTeam(row)}
            >
              {row.name}
            </button>
            {row.description && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                {row.description}
              </p>
            )}
          </div>
        ),
      },
      {
        id: 'memberCount',
        header: 'Members',
        sortable: true,
        cell: (row) => (
          <span className="text-sm text-gray-700 dark:text-gray-300">{row.memberCount}</span>
        ),
      },
      {
        id: 'lead',
        header: 'Lead',
        cell: (row) => (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {row.leadUserName || '—'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortable: true,
        cell: (row) => (
          <Badge variant={row.isActive ? 'success' : 'default'} size="sm" dot>
            {row.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (row) => (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => handleEditTeam(row)}>
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toggleStatusMutation.mutate({ id: row.id, isActive: !row.isActive })}
            >
              {row.isActive ? 'Deactivate' : 'Activate'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDeletingTeam(row)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [toggleStatusMutation],
  );

  const pagination: PaginationState | undefined = teamsData
    ? { page: teamsData.page, pageSize: teamsData.pageSize, total: teamsData.total }
    : undefined;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Teams"
          description="Create and manage teams. Teams are used to group users and assign data visibility roles."
          action={
            <Button size="sm" onClick={handleNewTeam}>
              New Team
            </Button>
          }
        />
        <CardContent>
          <div className="mb-4">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
              placeholder="All statuses"
            />
          </div>
          <DataTable<Team>
            data={teamsData?.teams ?? []}
            columns={columns}
            keyExtractor={(row) => row.id}
            sort={sort}
            onSortChange={handleSortChange}
            pagination={pagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={isLoading}
            emptyMessage="No teams found. Create one to get started."
            aria-label="Teams table"
          />
        </CardContent>
      </Card>

      {/* Create / Edit Team Modal */}
      {isFormOpen && (
        <TeamFormModal
          team={editingTeam}
          teams={allTeams ?? []}
          users={users ?? []}
          onSave={handleSaveTeam}
          onClose={() => {
            setIsFormOpen(false);
            setEditingTeam(null);
          }}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Team Detail / Members Modal */}
      {detailTeam && (
        <TeamDetailModal
          team={detailTeam}
          users={users ?? []}
          onClose={() => setDetailTeam(null)}
        />
      )}

      {/* Delete Confirmation */}
      {deletingTeam && (
        <Dialog
          open
          onClose={() => setDeletingTeam(null)}
          onConfirm={() => deactivateMutation.mutate(deletingTeam.id)}
          title="Delete Team"
          description={`Are you sure you want to delete "${deletingTeam.name}"? This will remove all member associations.`}
          confirmLabel="Delete"
          confirmVariant="danger"
          loading={deactivateMutation.isPending}
        />
      )}
    </div>
  );
}

// ─── Team Form Modal ────────────────────────────────────────────────────────────

interface TeamFormModalProps {
  team: Team | null;
  teams: Team[];
  users: UserOption[];
  onSave: (data: CreateTeamData | UpdateTeamData) => void;
  onClose: () => void;
  isSaving: boolean;
}

function TeamFormModal({ team, teams, users, onSave, onClose, isSaving }: TeamFormModalProps) {
  const [form, setForm] = useState({
    name: team?.name ?? '',
    description: team?.description ?? '',
    parentTeamId: team?.parentTeamId ?? '',
    leadUserId: team?.leadUserId ?? '',
    isActive: team?.isActive ?? true,
  });

  const parentTeamOptions = useMemo(
    () => teams.filter((t) => t.id !== team?.id).map((t) => ({ value: t.id, label: t.name })),
    [teams, team],
  );

  const userOptions = useMemo(
    () => users.map((u) => ({ value: u.id, label: `${u.name} (${u.email})` })),
    [users],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      parentTeamId: form.parentTeamId || null,
      leadUserId: form.leadUserId || null,
      ...(team ? { isActive: form.isActive } : {}),
    };
    onSave(payload as CreateTeamData | UpdateTeamData);
  };

  return (
    <Modal open onClose={onClose} title={team ? `Edit Team: ${team.name}` : 'New Team'} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Team Name"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          required
          placeholder="e.g., Corporate Tax Team"
        />
        <Input
          label="Description"
          value={form.description}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          placeholder="Brief description of this team's purpose"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Parent Team"
            value={form.parentTeamId}
            onChange={(e) => setForm((prev) => ({ ...prev, parentTeamId: e.target.value }))}
            options={parentTeamOptions}
            placeholder="None (top-level)"
          />
          <Select
            label="Team Lead"
            value={form.leadUserId}
            onChange={(e) => setForm((prev) => ({ ...prev, leadUserId: e.target.value }))}
            options={userOptions}
            placeholder="Select lead (optional)"
          />
        </div>

        {team && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="team-active"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
            />
            <label htmlFor="team-active" className="text-sm text-gray-700 dark:text-gray-300">
              Active
            </label>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSaving} disabled={!form.name.trim()}>
            {team ? 'Update Team' : 'Create Team'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Team Detail / Members Modal ────────────────────────────────────────────────

interface TeamDetailModalProps {
  team: Team;
  users: UserOption[];
  onClose: () => void;
}

function TeamDetailModal({ team, users, onClose }: TeamDetailModalProps) {
  const queryClient = useQueryClient();
  const [addMemberUserId, setAddMemberUserId] = useState('');
  const [addMemberRole, setAddMemberRole] = useState('');

  const { data: teamDetail, isLoading } = useQuery({
    queryKey: ['settings', 'team-detail', team.id],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<TeamDetail>>(`/teams/${team.id}`);
      return response.data.data;
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: async ({ userId, jobRole }: { userId: string; jobRole: string }) => {
      await apiClient.post(`/teams/${team.id}/members`, { userId, job_role: jobRole });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'team-detail', team.id] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'teams'] });
      setAddMemberUserId('');
      setAddMemberRole('');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async ({ userId, jobRole }: { userId: string; jobRole: string }) => {
      await apiClient.delete(`/teams/${team.id}/members/${userId}?job_role=${jobRole}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'team-detail', team.id] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'teams'] });
    },
  });

  // Build set of existing user+role combos for duplicate prevention
  const existingMemberKeys = useMemo(
    () => new Set(teamDetail?.members?.map((m) => `${m.userId}:${m.jobRole}`) ?? []),
    [teamDetail],
  );

  const availableUsers = useMemo(
    () => users.map((u) => ({ value: u.id, label: `${u.name} (${u.email})` })),
    [users],
  );

  // Check if the selected user+role combo already exists
  const isDuplicate =
    addMemberUserId && addMemberRole
      ? existingMemberKeys.has(`${addMemberUserId}:${addMemberRole}`)
      : false;

  const handleAddMember = (e: React.FormEvent) => {
    e.preventDefault();
    if (addMemberUserId && addMemberRole && !isDuplicate) {
      addMemberMutation.mutate({ userId: addMemberUserId, jobRole: addMemberRole });
    }
  };

  return (
    <Modal open onClose={onClose} title={`Team: ${team.name}`} size="lg">
      <div className="space-y-6 max-h-[70vh] overflow-y-auto">
        {/* Team Info */}
        <div className="space-y-2">
          {team.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400">{team.description}</p>
          )}
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <span>
              Status:{' '}
              <Badge variant={team.isActive ? 'success' : 'default'} size="sm" dot>
                {team.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </span>
            {team.leadUserName && <span>Lead: {team.leadUserName}</span>}
          </div>
        </div>

        {/* Members Section */}
        <div>
          <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
            Members ({teamDetail?.members?.length ?? 0})
          </h4>

          {/* Add Member Form */}
          <form onSubmit={handleAddMember} className="mb-4 flex items-end gap-2">
            <div className="flex-1">
              <Select
                label="User"
                value={addMemberUserId}
                onChange={(e) => setAddMemberUserId(e.target.value)}
                options={availableUsers}
                placeholder="Select a user..."
              />
            </div>
            <div className="w-40">
              <Select
                label="Role"
                value={addMemberRole}
                onChange={(e) => setAddMemberRole(e.target.value)}
                options={JOB_ROLE_OPTIONS}
                placeholder="Select role..."
              />
            </div>
            <Button
              type="submit"
              size="sm"
              disabled={!addMemberUserId || !addMemberRole || isDuplicate}
              loading={addMemberMutation.isPending}
              title={isDuplicate ? 'This user already has this role in this team' : undefined}
            >
              Add
            </Button>
          </form>
          {isDuplicate && (
            <p className="mb-3 text-xs text-danger-500">
              This user already exists in this team with the selected role.
            </p>
          )}

          {/* Members List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
            </div>
          ) : !teamDetail?.members || teamDetail.members.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
              No members yet. Add users to this team above.
            </p>
          ) : (
            <div className="divide-y divide-border dark:divide-border-dark rounded-lg border border-border dark:border-border-dark">
              {teamDetail.members.map((member) => (
                <div
                  key={`${member.userId}-${member.jobRole}`}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {member.name}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{member.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="info" size="sm">
                      {member.jobRole?.charAt(0).toUpperCase() + member.jobRole?.slice(1) ||
                        'Others'}
                    </Badge>
                    {team.leadUserId === member.userId && (
                      <Badge variant="success" size="sm">
                        Lead
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        removeMemberMutation.mutate({
                          userId: member.userId,
                          jobRole: member.jobRole,
                        })
                      }
                      loading={removeMemberMutation.isPending}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Close Button */}
        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
